/**
 * GateKeysPage — Compliance · Gate Keys.
 *
 * Canonical catalog of admin-configurable enforcement gates. Each gate is the
 * FK target for EntityRuleGateEnforcement.GateKey, and every row cites a
 * ProcedureCitation (citationKey FK) per CLAUDE.md rule #1 — "do not guess
 * about legacy procedure."
 *
 * Reads:
 *   client.retail.config.gateKeys()            // Record<string,unknown> rows → local GateKeyRow cast
 *   client.retail.config.procedureCitations()  // FK source for the citationKey dropdown
 *
 * Writes go through the typed bearer-only admin surface (FIRST ARG '' = no
 * bypass key; bearer auth carries the call):
 *   client.retail.admin.compliance.gateKeys.upsert('', body)
 *   client.retail.admin.compliance.gateKeys.remove('', key)
 *
 * Mirrors apps/ROP-teams/src/modules/Admin/editors/gateKeysEditor.tsx for the
 * category / scope enums, the allowedStrategiesJson control + example hint, and
 * the citationKey FK dropdown. Structure matches the ProductsPage CRUD pattern.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import type { GateKeyUpsertBody } from '@retail-ops/api-client';

// ── Wire shapes ───────────────────────────────────────────────────────────

// Rows as returned by /v3/config/gate-keys (Tier-1 paired read — typed on the
// wire as Record<string,unknown>). Cast locally to the actual EF GateKey shape.
interface GateKeyRow {
  key: string;
  displayName: string;
  category: string | null;
  description: string | null;
  scope: string | null;
  allowedStrategiesJson: string | null;
  defaultStrategy: string | null;
  defaultDeferralDays: number | null;
  citationKey: string | null;
  sortOrder: number | null;
  isActive: boolean;
}

// Citation rows from /v3/config/procedure-citations — only the two fields the
// FK dropdown needs.
interface CitationRow {
  citationKey: string;
  displayLabel: string | null;
}

// ── Enums — copied VERBATIM from the legacy gateKeysEditor (do NOT invent) ──

// Category ENUM: BSA | CIP | Risk | Product | Workflow | Disclosure | Signing | DocCapture | Other
const CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'BSA',        label: 'BSA — OFAC / LexisNexis / BO Form' },
  { value: 'CIP',        label: 'CIP — Customer Identification (F254-258 / F92 / F348)' },
  { value: 'Risk',       label: 'Risk — Risk Mgmt pre-review / High-Dollar / LitHold' },
  { value: 'Product',    label: 'Product — per-product validation gates' },
  { value: 'Workflow',   label: 'Workflow — Submit / Save-Close / Decline / Cancel' },
  { value: 'Disclosure', label: 'Disclosure — disclosure rendering / preview' },
  { value: 'Signing',    label: 'Signing — routing / wet-sign / resend / abort' },
  { value: 'DocCapture', label: 'DocCapture — WizardDocMatrix REQUIRED tiles' },
  { value: 'Other',      label: 'Other — misc' },
];

// Scope ENUM: subtype | product | role | global
const SCOPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'subtype', label: 'subtype — strategy varies per CustomerType' },
  { value: 'product', label: 'product — strategy varies per Product' },
  { value: 'role',    label: 'role — strategy varies per UserRole' },
  { value: 'global',  label: 'global — single global setting' },
];

// Default allowedStrategiesJson seed for new rows (matches the legacy editor's
// EMPTY constant). The hint shows the canonical strategy vocabulary.
const DEFAULT_ALLOWED_STRATEGIES_JSON =
  '["required-hard-stop","warn-only","deferred-N-days-post-acctadd","officer-override"]';
const ALLOWED_STRATEGIES_HINT =
  'JSON array of allowed strategies, e.g. ["required-hard-stop","warn-only","deferred-N-days","officer-override"].';

// ── Draft model ────────────────────────────────────────────────────────────

interface EditDraft {
  isNew: boolean;
  key: string;
  displayName: string;
  category: string;
  description: string;
  scope: string;
  allowedStrategiesJson: string;
  defaultStrategy: string;
  defaultDeferralDays: number | null;
  citationKey: string;
  sortOrder: number | null;
  isActive: boolean;
}

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function toStr(v: unknown): string {
  return v == null ? '' : String(v);
}

function rowToDraft(r: GateKeyRow): EditDraft {
  return {
    isNew: false,
    key: r.key,
    displayName: r.displayName ?? '',
    category: r.category ?? 'Workflow',
    description: r.description ?? '',
    scope: r.scope ?? 'subtype',
    allowedStrategiesJson: r.allowedStrategiesJson ?? DEFAULT_ALLOWED_STRATEGIES_JSON,
    defaultStrategy: r.defaultStrategy ?? '',
    defaultDeferralDays: toNum(r.defaultDeferralDays),
    citationKey: r.citationKey ?? '',
    sortOrder: toNum(r.sortOrder),
    isActive: r.isActive,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    key: '',
    displayName: '',
    category: 'Workflow',
    description: '',
    scope: 'subtype',
    allowedStrategiesJson: DEFAULT_ALLOWED_STRATEGIES_JSON,
    defaultStrategy: 'required-hard-stop',
    defaultDeferralDays: null,
    citationKey: '',
    sortOrder: 100,
    isActive: true,
  };
}

function draftToBody(d: EditDraft): GateKeyUpsertBody {
  const nullable = (s: string): string | null => {
    const t = s.trim();
    return t === '' ? null : t;
  };
  return {
    key: d.key.trim(),
    displayName: d.displayName.trim(),
    category: nullable(d.category),
    description: nullable(d.description),
    scope: nullable(d.scope),
    allowedStrategiesJson: nullable(d.allowedStrategiesJson),
    defaultStrategy: nullable(d.defaultStrategy),
    defaultDeferralDays: d.defaultDeferralDays,
    citationKey: nullable(d.citationKey),
    sortOrder: d.sortOrder,
    isActive: d.isActive,
  };
}

// Map a wire Record<string,unknown> row to the local typed shape.
function castGateKeyRow(raw: Record<string, unknown>): GateKeyRow {
  return {
    key: toStr(raw.key),
    displayName: toStr(raw.displayName),
    category: raw.category == null ? null : String(raw.category),
    description: raw.description == null ? null : String(raw.description),
    scope: raw.scope == null ? null : String(raw.scope),
    allowedStrategiesJson:
      raw.allowedStrategiesJson == null ? null : String(raw.allowedStrategiesJson),
    defaultStrategy: raw.defaultStrategy == null ? null : String(raw.defaultStrategy),
    defaultDeferralDays: toNum(raw.defaultDeferralDays),
    citationKey: raw.citationKey == null ? null : String(raw.citationKey),
    sortOrder: toNum(raw.sortOrder),
    isActive: raw.isActive === true || raw.isActive === 1 || raw.isActive === '1',
  };
}

function castCitationRow(raw: Record<string, unknown>): CitationRow {
  return {
    citationKey: toStr(raw.citationKey),
    displayLabel: raw.displayLabel == null ? null : String(raw.displayLabel),
  };
}

export function GateKeysPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<GateKeyRow> | null>(null);
  const [citations, setCitations] = useState<ReadonlyArray<CitationRow>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [gateResp, citationResp] = await Promise.all([
        client.retail.config.gateKeys(),
        client.retail.config.procedureCitations(),
      ]);
      if (gateResp.success && Array.isArray(gateResp.data)) {
        setRows(gateResp.data.map(castGateKeyRow));
      } else {
        setError(gateResp.error ?? 'Failed to load gate keys.');
        return;
      }
      if (citationResp.success && Array.isArray(citationResp.data)) {
        setCitations(citationResp.data.map(castCitationRow));
      } else {
        setCitations([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // FK dropdown options for citationKey — built from the live procedure
  // citations catalog (NOT free-text). "— pick a citation —" maps to empty.
  const citationOptions = useMemo(
    () => [
      { value: '', label: '— pick a citation —' },
      ...citations.map((c) => ({
        value: c.citationKey,
        label: `${c.citationKey} — ${c.displayLabel ?? ''}`,
      })),
    ],
    [citations],
  );

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter((r) =>
      r.key.toLowerCase().includes(n) ||
      r.displayName.toLowerCase().includes(n) ||
      (r.category ?? '').toLowerCase().includes(n) ||
      (r.scope ?? '').toLowerCase().includes(n) ||
      (r.citationKey ?? '').toLowerCase().includes(n)
    );
  }, [rows, filter]);

  const remove = useCallback(
    async (key: string): Promise<void> => {
      if (
        !window.confirm(
          `Soft-delete gate '${key}'? FK references on EntityRuleGateEnforcement rows remain intact.`,
        )
      ) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.compliance.gateKeys.remove('', key);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<GateKeyRow>> = useMemo(() => [
    {
      header: 'Status',
      cell: (r) =>
        r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
    },
    {
      header: 'Key',
      cell: (r) => <code className="cell-mono--bold">{r.key}</code>,
    },
    { header: 'Display Name', cell: (r) => r.displayName },
    {
      header: 'Category',
      cell: (r) =>
        r.category ? <Pill tone="muted">{r.category}</Pill> : <span className="cell-dim">—</span>,
    },
    {
      header: 'Scope',
      cell: (r) =>
        r.scope ? <code className="cell-mono">{r.scope}</code> : <span className="cell-dim">—</span>,
    },
    {
      header: 'Default Strategy',
      cell: (r) =>
        r.defaultStrategy ? (
          <code className="cell-mono">{r.defaultStrategy}</code>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Citation',
      cell: (r) =>
        r.citationKey ? (
          <code className="cell-mono">{r.citationKey}</code>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Order',
      cell: (r) =>
        r.sortOrder != null ? (
          <span className="cell-mono">{r.sortOrder}</span>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: '',
      cell: (r) => (
        <div className="cell-actions">
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void remove(r.key)}>
            Delete
          </Button>
        </div>
      ),
    },
  ], [remove]);

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      // Validate required fields + JSON before the round-trip (mirrors legacy).
      if (!d.key.trim() || !d.displayName.trim()) {
        setSaveErr('Key and display name are required.');
        setSaving(false);
        return;
      }
      const allowed = d.allowedStrategiesJson.trim();
      if (allowed !== '') {
        try {
          JSON.parse(allowed);
        } catch {
          setSaveErr(
            'Allowed strategies must be valid JSON, e.g. ["required-hard-stop","warn-only"].',
          );
          setSaving(false);
          return;
        }
      }
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.compliance.gateKeys.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load gate keys: {error}</Banner>;
  if (!rows) return <Spinner label="Loading gate keys…" />;

  return (
    <>
      <Card
        title={`Gate Keys — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by key, name, category, scope, or citation…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <DataTable<GateKeyRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.key}
          emptyState={<p className="empty">No gate keys match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft ? (draft.isNew ? 'New gate key' : `Edit gate key — ${draft.key}`) : ''}
        onClose={() => (saving ? undefined : setDraft(null))}
        width={620}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Key"
              value={draft.key}
              onChange={(v) => setDraft({ ...draft, key: v })}
              disabled={!draft.isNew}
              monospace
              required
              hint={
                draft.isNew
                  ? 'Primary key (PascalCase, e.g. AddressVerification). Read-only after create.'
                  : 'Read-only after create.'
              }
            />
            <TextField
              label="Display Name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v })}
              required
            />
            <SelectField
              label="Category"
              value={draft.category}
              onChange={(v) => setDraft({ ...draft, category: v })}
              options={CATEGORY_OPTIONS}
            />
            <SelectField
              label="Scope"
              value={draft.scope}
              onChange={(v) => setDraft({ ...draft, scope: v })}
              options={SCOPE_OPTIONS}
            />
            <TextArea
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={3}
              placeholder="What this gate enforces; cite the procedure source."
            />
            <TextArea
              label="Allowed Strategies (JSON array)"
              value={draft.allowedStrategiesJson}
              onChange={(v) => setDraft({ ...draft, allowedStrategiesJson: v })}
              rows={2}
              placeholder={DEFAULT_ALLOWED_STRATEGIES_JSON}
              hint={ALLOWED_STRATEGIES_HINT}
            />
            <TextField
              label="Default Strategy"
              value={draft.defaultStrategy}
              onChange={(v) => setDraft({ ...draft, defaultStrategy: v })}
              monospace
              hint="One value from the allowed-strategies array (e.g. required-hard-stop)."
            />
            <NumberField
              label="Default Deferral Days"
              value={draft.defaultDeferralDays}
              onChange={(v) => setDraft({ ...draft, defaultDeferralDays: v })}
              min={0}
              hint="Only used when the default strategy defers N days."
            />
            <SelectField
              label="Citation Key"
              value={draft.citationKey}
              onChange={(v) => setDraft({ ...draft, citationKey: v })}
              options={citationOptions}
              hint="FK to the Procedure Citations catalog (CLAUDE.md rule #1 — every gate cites a source)."
            />
            <NumberField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Show in the admin-config gate picker."
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
